import dayjs from 'dayjs';
import React from 'react';
import * as pdf from 'react-pdf';

import { Box, IconButton, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import DateElement from '../elements/DateElement';
import useLocales from '../../../hooks/useLocales';
import ElementLabel from '../elements/ElementLabel';
import ElementWrapper from '../elements/ElementWrapper';
import CheckboxElement from '../elements/CheckboxElement';
import UserDataElement from '../elements/UserDataElement';
import DropdownElement from '../elements/DropdownElement';
import DateRangeElement from '../elements/DateRangeElement';
import HyperlinkElement from '../elements/HyperlinkElement';
import SignatureElement from '../elements/SignatureElement';
import ElementSettingsDialog from './ElementSettingsDialog';
import StaticTextElement from '../elements/StaticTextElement';
import InputFieldElement from '../elements/InputFieldElement';
import CustomListElement from '../elements/CustomListElement';
import RadioButtonElement from '../elements/RadioButtonElement';
import AddressBookElement from '../elements/AddressBookElement';
import { handleTextAlign } from '../../../utils/forms/formBuilderUtils';
import layerBackground from '../../../assets/layer_background_pattern.png';
import { FormElementType } from '../../../@types/forms/enums/FormElementType';
import RegistrationNumberElement from '../elements/RegistrationNumberElement';
import { Collaborator, ElementProps, FormViewportProps } from '../../../@types/forms/types';

const { Page, Document } = pdf;

// ----------------------------------------------------------------------

type FormPdfViewerProps = {
  file: any;
  numPages: number | null;
  pageNumber: number;
  isEditMode: boolean;
  elements: ElementProps[];
  collaborators: Collaborator[];
  formViewport: FormViewportProps[] | null;
  pageNumberScale: number;
  layerSize: { width: number; height: number } | null;
  isDialogOpen: boolean;
  selectedElement: ElementProps | null;
  elementLayer: React.RefObject<HTMLElement | null>;
  onDocumentLoadSuccess: (pdf: any) => void;
  handlePdfNavButtonClick: (direction: number) => void;
  handleSettingsButtonClick: (el: ElementProps) => void;
  handleDragStart: (id: string) => void;
  handleDragStop: (id: string, d: any) => void;
  handleResizeStart: (id: string) => void;
  handleResizeStop: (id: string, delta: any, position: any) => void;
  handleDeleteElement: (id?: string) => void;
  handleElementChange: (el: ElementProps) => void;
  setElements: (elements: ElementProps[]) => void;
  setIsDialogOpen: (open: boolean) => void;
};

const FormPdfViewer: React.FC<FormPdfViewerProps> = ({
  file,
  numPages,
  pageNumber,
  isEditMode,
  elements,
  collaborators,
  formViewport,
  pageNumberScale,
  layerSize,
  isDialogOpen,
  selectedElement,
  elementLayer,
  onDocumentLoadSuccess,
  handlePdfNavButtonClick,
  handleSettingsButtonClick,
  handleDragStart,
  handleDragStop,
  handleResizeStart,
  handleResizeStop,
  handleDeleteElement,
  handleElementChange,
  setElements,
  setIsDialogOpen,
}) => {
  const { t } = useLocales();
  const defaultToday = dayjs().format('YYYY-MM-DD');

  const filteredElements = elements.filter((element) => element.page === pageNumber);

  return (
    <>
      {/* PDF NAVIGATION - TOP */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          alignItems: 'center',
          mb: 1,
        }}
      >
        <IconButton
          aria-label="arrow-back"
          size="large"
          disabled={pageNumber === 1}
          onClick={() => handlePdfNavButtonClick(-1)}
        >
          <ArrowBackIosNewIcon />
        </IconButton>

        <Typography>
          {t('forms.documentNavigation.page')} {pageNumber} {t('forms.documentNavigation.of')}{' '}
          {numPages}
        </Typography>

        <IconButton
          aria-label="arrow-forward"
          size="large"
          disabled={pageNumber === numPages}
          onClick={() => handlePdfNavButtonClick(1)}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {/* PDF DOCUMENT */}
        <Box>
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            externalLinkTarget="_blank"
          >
            <Page
              pageNumber={pageNumber}
              width={layerSize !== null ? layerSize.width : undefined}
              renderTextLayer={false}
            />
          </Document>
        </Box>

        {/* LAYER FOR DRAG & DROP ELEMENTS */}
        <Box
          ref={elementLayer}
          sx={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            },
            isEditMode
              ? {
                  backgroundImage: `url(${layerBackground})`,
                }
              : {
                  backgroundImage: 'none',
                },
          ]}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 1,
              zIndex: 1000,
              overflow: 'hidden',
            }}
          >
            {/* EDIT MODE - Elements with wrappers */}
            {isEditMode && elements.length > 0 && formViewport && (
              <>
                {filteredElements.map((el) => {
                  // Skip elements that have special rendering
                  const skipDefaultRender = [
                    FormElementType.Image,
                    FormElementType.StaticText,
                    FormElementType.Checkbox,
                    FormElementType.RadioButton,
                    FormElementType.Date,
                    FormElementType.DateRange,
                    FormElementType.Hyperlink,
                    FormElementType.Signature,
                    FormElementType.User,
                    FormElementType.RegistrationNumber,
                  ].includes(el.type);

                  const commonProps = {
                    element: el,
                    pageNumberScale,
                    isDialogOpen,
                    handleSettingsButtonClick,
                    handleDragStart,
                    handleDragStop,
                    handleResizeStart,
                    handleResizeStop,
                    handleDeleteElement,
                  };

                  // Default element (InputField, Dropdown, AddressBook, CustomList)
                  if (!skipDefaultRender) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <ElementLabel
                          el={el}
                          label={el.label}
                          pageNumberScale={pageNumberScale}
                          collaboratorColor={collaborator?.color}
                        />
                      </ElementWrapper>
                    );
                  }

                  // Image element
                  if (el.type === FormElementType.Image) {
                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        {el.file && <img src={`${el.file}`} alt={el.label} />}
                        {!el.file && (
                          <ElementLabel el={el} label={el.label} pageNumberScale={pageNumberScale} />
                        )}
                      </ElementWrapper>
                    );
                  }

                  // StaticText element
                  if (el.type === FormElementType.StaticText) {
                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <StaticTextElement el={el} pageNumberScale={pageNumberScale} />
                      </ElementWrapper>
                    );
                  }

                  // Checkbox element
                  if (el.type === FormElementType.Checkbox) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <CheckboxElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage={false}
                          collaboratorColor={collaborator?.color}
                        />
                      </ElementWrapper>
                    );
                  }

                  // RadioButton element
                  if (el.type === FormElementType.RadioButton) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <RadioButtonElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage={false}
                          collaboratorColor={collaborator?.color}
                        />
                      </ElementWrapper>
                    );
                  }

                  // Date element
                  if (el.type === FormElementType.Date) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <DateElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage={false}
                          collaboratorColor={collaborator?.color}
                        />
                      </ElementWrapper>
                    );
                  }

                  // DateRange element
                  if (el.type === FormElementType.DateRange) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <DateRangeElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage={false}
                          collaboratorColor={collaborator?.color}
                        />
                      </ElementWrapper>
                    );
                  }

                  // Hyperlink element
                  if (el.type === FormElementType.Hyperlink) {
                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <HyperlinkElement el={el} pageNumberScale={pageNumberScale} />
                      </ElementWrapper>
                    );
                  }

                  // RegistrationNumber element
                  if (el.type === FormElementType.RegistrationNumber) {
                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <RegistrationNumberElement el={el} pageNumberScale={pageNumberScale} />
                      </ElementWrapper>
                    );
                  }

                  // Signature element
                  if (el.type === FormElementType.Signature) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <SignatureElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage={false}
                          collaboratorColor={collaborator?.color}
                        />
                      </ElementWrapper>
                    );
                  }

                  // User element
                  if (el.type === FormElementType.User) {
                    return (
                      <ElementWrapper key={el.id} {...commonProps}>
                        <UserDataElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage={false}
                        />
                      </ElementWrapper>
                    );
                  }

                  return null;
                })}
              </>
            )}

            {/* PREVIEW MODE - Elements without wrappers */}
            {!isEditMode && elements && elements.length > 0 && formViewport && (
              <>
                {filteredElements.map((el) => {
                  const scaledDimensions = {
                    x: el.x * pageNumberScale,
                    y: el.y * pageNumberScale,
                    width: el.width * pageNumberScale,
                    height: el.height * pageNumberScale,
                    fontSize: el.fontSize * pageNumberScale,
                  };

                  const commonBoxStyles = {
                    position: 'absolute' as const,
                    top: scaledDimensions.y,
                    left: scaledDimensions.x,
                    width: scaledDimensions.width,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'flex-end',
                  };

                  // StaticText element
                  if (el.type === FormElementType.StaticText) {
                    return (
                      <Box key={el.id} sx={commonBoxStyles}>
                        <StaticTextElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isMobileView={false}
                        />
                      </Box>
                    );
                  }

                  // InputField element
                  if (el.type === FormElementType.InputField) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <Box key={el.id} sx={commonBoxStyles}>
                        <InputFieldElement
                          isChild={el.isChild}
                          el={el}
                          scaledDimensions={scaledDimensions}
                          handleTextAlign={handleTextAlign}
                          handleInputFieldChange={() => {}}
                          pageNumberScale={pageNumberScale}
                          collaboratorColor={collaborator?.color}
                        />
                      </Box>
                    );
                  }

                  // Dropdown element
                  if (el.type === FormElementType.Dropdown) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <Box key={el.id} sx={commonBoxStyles}>
                        <DropdownElement
                          el={el}
                          scaledDimensions={scaledDimensions}
                          handleTextAlign={handleTextAlign}
                          handleDropdownChange={() => {}}
                          pageNumberScale={pageNumberScale}
                          collaboratorColor={collaborator?.color}
                        />
                      </Box>
                    );
                  }

                  // Checkbox element
                  if (el.type === FormElementType.Checkbox) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <Box
                        key={el.id}
                        sx={{
                          position: 'absolute',
                          top: scaledDimensions.y,
                          left: scaledDimensions.x,
                          width: scaledDimensions.width,
                        }}
                      >
                        <CheckboxElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          handleCheckboxChange={() => {}}
                          isFormFillerPage
                          collaboratorColor={collaborator?.color}
                        />
                      </Box>
                    );
                  }

                  // RadioButton element
                  if (el.type === FormElementType.RadioButton) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <Box
                        key={el.id}
                        sx={{
                          position: 'absolute',
                          top: scaledDimensions.y,
                          left: scaledDimensions.x,
                          width: scaledDimensions.width,
                        }}
                      >
                        <RadioButtonElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          handleRadioButtonChange={() => {}}
                          isFormFillerPage
                          collaboratorColor={collaborator?.color}
                        />
                      </Box>
                    );
                  }

                  // Date element
                  if (el.type === FormElementType.Date) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <Box
                        key={el.id}
                        sx={{
                          position: 'absolute',
                          top: scaledDimensions.y,
                          left: scaledDimensions.x,
                          width: scaledDimensions.width,
                        }}
                      >
                        <DateElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage
                          elementContentValue={dayjs(defaultToday)}
                          handleDateChange={() => {}}
                          collaboratorColor={collaborator?.color}
                        />
                      </Box>
                    );
                  }

                  // DateRange element
                  if (el.type === FormElementType.DateRange) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <Box
                        key={el.id}
                        sx={{
                          position: 'absolute',
                          top: scaledDimensions.y,
                          left: scaledDimensions.x,
                          width: scaledDimensions.width,
                        }}
                      >
                        <DateRangeElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage
                          elementContentValue={[dayjs(defaultToday), dayjs(defaultToday)]}
                          handleDateRangeChange={() => {}}
                          collaboratorColor={collaborator?.color}
                        />
                      </Box>
                    );
                  }

                  // Image element
                  if (el.type === FormElementType.Image) {
                    return (
                      <Box key={el.id} sx={commonBoxStyles}>
                        <img
                          src={`${el.file}`}
                          alt={`${el.label}`}
                          width={el.width * pageNumberScale}
                        />
                      </Box>
                    );
                  }

                  // CustomList element
                  if (el.type === FormElementType.CustomList) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <Box key={el.id} sx={commonBoxStyles}>
                        <CustomListElement
                          el={el}
                          scaledDimensions={scaledDimensions}
                          handleTextAlign={handleTextAlign}
                          handleDropdownChange={() => {}}
                          pageNumberScale={pageNumberScale}
                          collaboratorColor={collaborator?.color}
                        />
                      </Box>
                    );
                  }

                  // AddressBook element
                  if (el.type === FormElementType.AddressBook) {
                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <Box key={el.id} sx={commonBoxStyles}>
                        <AddressBookElement
                          el={el}
                          groupedUsers={[]}
                          handleAddressBookChange={() => {}}
                          pageNumberScale={pageNumberScale}
                          collaboratorColor={collaborator?.color}
                        />
                      </Box>
                    );
                  }

                  // Hyperlink element
                  if (el.type === FormElementType.Hyperlink) {
                    return (
                      <Box key={el.id} sx={commonBoxStyles}>
                        <HyperlinkElement el={el} pageNumberScale={pageNumberScale} />
                      </Box>
                    );
                  }

                  // Signature element
                  if (el.type === FormElementType.Signature) {
                    const size = {
                      width: scaledDimensions.width,
                      height: scaledDimensions.height,
                    };

                    const collaborator = el.collaboratorId
                      ? collaborators.find((c) => c.collaboratorId === el.collaboratorId)
                      : undefined;

                    return (
                      <Box
                        key={el.id}
                        sx={{
                          position: 'absolute',
                          top: scaledDimensions.y,
                          left: scaledDimensions.x,
                          width: scaledDimensions.width,
                          height: scaledDimensions.height,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'flex-end',
                        }}
                      >
                        <SignatureElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage
                          handleSignatureChange={() => {}}
                          size={size}
                          collaboratorColor={collaborator?.color}
                        />
                      </Box>
                    );
                  }

                  // User element
                  if (el.type === FormElementType.User) {
                    return (
                      <Box
                        key={el.id}
                        sx={{
                          position: 'absolute',
                          top: scaledDimensions.y,
                          left: scaledDimensions.x,
                          width: scaledDimensions.width,
                          height: scaledDimensions.height,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'flex-end',
                        }}
                      >
                        <UserDataElement
                          el={el}
                          pageNumberScale={pageNumberScale}
                          isFormFillerPage
                        />
                      </Box>
                    );
                  }

                  return null;
                })}
              </>
            )}
          </Box>

          <ElementSettingsDialog
            open={isDialogOpen}
            handleClose={setIsDialogOpen}
            handleDelete={handleDeleteElement}
            element={selectedElement}
            elements={elements}
            collaborators={collaborators}
            setElements={setElements}
            handleChange={handleElementChange}
          />
        </Box>
      </Box>

      {/* PDF NAVIGATION - BOTTOM */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          alignItems: 'center',
          mt: 1,
        }}
      >
        <IconButton
          aria-label="arrow-back"
          size="large"
          disabled={pageNumber === 1}
          onClick={() => handlePdfNavButtonClick(-1)}
        >
          <ArrowBackIosNewIcon />
        </IconButton>

        <Typography>
          {t('forms.documentNavigation.page')} {pageNumber} {t('forms.documentNavigation.of')}{' '}
          {numPages}
        </Typography>

        <IconButton
          aria-label="arrow-forward"
          size="large"
          disabled={pageNumber === numPages}
          onClick={() => handlePdfNavButtonClick(1)}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>
    </>
  );
};

export default FormPdfViewer;
